import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from './api';
import type { Rol, Usuario } from './tipos';

interface Sesion {
  usuario: Usuario | null;
  cargando: boolean;
  entrar: (email: string, password: string) => Promise<void>;
  salir: () => Promise<void>;
  refrescar: () => Promise<void>;
  es: (...roles: Rol[]) => boolean;
}

const Ctx = createContext<Sesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  const refrescar = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: Usuario }>('/auth/me');
      setUsuario(user);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setUsuario(null);
      else throw e;
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void refrescar();
  }, [refrescar]);

  const entrar = useCallback(async (email: string, password: string) => {
    const { user } = await api.post<{ user: Usuario }>('/auth/login', { email, password });
    setUsuario(user);
  }, []);

  const salir = useCallback(async () => {
    await api.post('/auth/logout');
    setUsuario(null);
  }, []);

  const valor = useMemo<Sesion>(
    () => ({
      usuario,
      cargando,
      entrar,
      salir,
      refrescar,
      es: (...roles: Rol[]) => (usuario ? roles.includes(usuario.role) : false),
    }),
    [usuario, cargando, entrar, salir, refrescar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSesion debe usarse dentro de ProveedorSesion');
  return ctx;
}

/* ─────────────────────────────── Avisos ─────────────────────────────────── */

type Tono = 'info' | 'hecho' | 'error';
interface Tostada {
  id: number;
  texto: string;
  tono: Tono;
}

const AvisosCtx = createContext<{
  avisar: (texto: string, tono?: Tono) => void;
} | null>(null);

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [lista, setLista] = useState<Tostada[]>([]);

  const avisar = useCallback((texto: string, tono: Tono = 'info') => {
    const id = Date.now() + Math.random();
    setLista((l) => [...l, { id, texto, tono }]);
    setTimeout(() => setLista((l) => l.filter((t) => t.id !== id)), 5200);
  }, []);

  return (
    <AvisosCtx.Provider value={{ avisar }}>
      {children}
      <div className="tostadas" role="status" aria-live="polite">
        {lista.map((t) => (
          <div key={t.id} className="tostada" data-tono={t.tono}>
            <span>{t.texto}</span>
          </div>
        ))}
      </div>
    </AvisosCtx.Provider>
  );
}

export function useAvisos() {
  const ctx = useContext(AvisosCtx);
  if (!ctx) throw new Error('useAvisos debe usarse dentro de ProveedorAvisos');
  return ctx;
}

/* ───────────────────────── Carga de datos ───────────────────────────────── */

export function useDatos<T>(
  cargar: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): { datos: T | null; error: string | null; cargando: boolean; recargar: () => void } {
  const [datos, setDatos] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [n, setN] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setCargando(true);
    setError(null);
    cargar(ac.signal)
      .then((d) => {
        if (!ac.signal.aborted) setDatos(d);
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setError(e instanceof ApiError ? e.message : 'No se han podido cargar los datos.');
      })
      .finally(() => {
        if (!ac.signal.aborted) setCargando(false);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);

  return { datos, error, cargando, recargar: () => setN((x) => x + 1) };
}
